import { useState } from 'react';
import ChevronRightIcon from '../../../assets/icons/chevron-right.svg?react';
import MailIcon from '../../../assets/icons/mail.svg?react';
import PlusIcon from '../../../assets/icons/plus.svg?react';
import SearchIcon from '../../../assets/icons/search.svg?react';
import TeamsIcon from '../../../assets/icons/teams.svg?react';
import {
  NotificationChannel,
  useGetNotificationChannelsQuery,
} from '../../../gql/generated';
import { Checkbox } from '../../Checkbox';
import { Input } from '../../Input';
import { AddChannelDialog } from '../AddChannelDialog/AddChannelDialog';
import { ChannelType } from './context';
import { useAddAlarmDialog } from './useAddAlarmDialog';

const CHANNEL_FILTERS = [
  { type: null, label: 'All', Icon: null },
  { type: 'Teams' as ChannelType, label: 'Teams', Icon: TeamsIcon },
  { type: 'Email' as ChannelType, label: 'Email', Icon: MailIcon },
];

export function AlarmChannelsStep() {
  const { formData, updateFormData } = useAddAlarmDialog();
  const [showAddChannelDialog, setShowAddChannelDialog] = useState(false);

  // Fetch notification channels from API
  const { data, loading, error, refetch } = useGetNotificationChannelsQuery();

  const toggleChannelSelection = (channelId: string) => {
    const isSelected = formData.selectedChannels.includes(channelId);
    updateFormData({
      selectedChannels: isSelected
        ? formData.selectedChannels.filter((id) => id !== channelId)
        : [...formData.selectedChannels, channelId],
    });
  };

  const toggleChannelTypeFilter = (type: ChannelType | null) => {
    // Single-select behavior: clicking the same chip deselects it (returns to All)
    const isActive =
      type === null
        ? formData.channelTypeFilters.length === 0
        : formData.channelTypeFilters.length === 1 &&
          formData.channelTypeFilters[0] === type;

    updateFormData({
      channelTypeFilters: isActive ? [] : type === null ? [] : [type],
    });
  };

  const handleChannelCreated = async (createdChannelId: string) => {
    // Refetch channels to get the updated list
    await refetch();
    // Automatically select the newly created channel
    updateFormData({
      selectedChannels: [...formData.selectedChannels, createdChannelId],
    });
  };

  const channels = data?.notificationChannels?.rows || [];

  const filteredChannels = channels
    .filter(
      (channel) =>
        channel.name
          .toLowerCase()
          .includes(formData.channelSearchQuery.toLowerCase()) &&
        (formData.channelTypeFilters.length === 0 ||
          formData.channelTypeFilters.includes(
            channel.channelType as ChannelType
          ))
    )
    .sort((a, b) => {
      // Sort selected channels to the top
      const aSelected = formData.selectedChannels.includes(a.id);
      const bSelected = formData.selectedChannels.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      // Keep original order for channels with same selection status
      return 0;
    });

  return (
    <>
      <div className='flex flex-col'>
        {/* Sticky Search & Filters */}
        <div
          className='bg-surface-primary sticky top-0 z-10 flex flex-col gap-4
            px-4 py-4 md:px-6'
        >
          {/* Search */}
          <div className='flex flex-col gap-1.5'>
            <div className='relative'>
              <div
                className='pointer-events-none absolute top-1/2 left-2
                  -translate-y-1/2'
              >
                <SearchIcon className='text-disabled h-4 w-4' />
              </div>
              <Input
                placeholder='Name, amount, date'
                value={formData.channelSearchQuery}
                onChange={(e) =>
                  updateFormData({ channelSearchQuery: e.target.value })
                }
                className='pl-8'
              />
            </div>
          </div>

          {/* Channel Type Filter Chips */}
          <div className='flex flex-wrap gap-2'>
            {CHANNEL_FILTERS.map(({ type, label, Icon }) => {
              const isActive =
                type === null
                  ? formData.channelTypeFilters.length === 0
                  : formData.channelTypeFilters.length === 1 &&
                    formData.channelTypeFilters.includes(type);
              return (
                <button
                  key={type ?? 'all'}
                  type='button'
                  onClick={() => toggleChannelTypeFilter(type)}
                  className={`flex items-center gap-1.5 rounded-full border
                  border-blue-100 px-3 py-2 transition-colors outline-none
                  focus-visible:ring-1 focus-visible:ring-blue-500
                  focus-visible:ring-offset-2 ${
                    isActive
                      ? 'text-primary bg-blue-100'
                      : `bg-surface-secondary text-secondary
                        hover:bg-surface-primary-support`
                  }`}
                >
                  {Icon && (
                    <Icon
                      className={`h-4 w-4
                      ${isActive ? 'text-primary' : 'text-secondary'}`}
                    />
                  )}
                  <span className='size-caption font-bold'>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Channels List */}
        <div className='border-border-secondary flex flex-col border-t'>
          {/* New Channel Option */}
          <button
            type='button'
            onClick={() => setShowAddChannelDialog(true)}
            className='border-border-secondary
              hover:bg-surface-primary-support/50 flex w-full cursor-pointer
              items-center justify-between gap-3 border-b px-5.5 py-3 text-left
              transition-colors outline-none focus-visible:ring-1
              focus-visible:ring-blue-500 focus-visible:ring-inset'
          >
            <div className='flex items-center gap-3'>
              <div className='flex h-5 w-5 items-center justify-center'>
                <PlusIcon className='text-focus h-5 w-5' />
              </div>
              <div className='flex flex-col'>
                <span className='size-p2 text-primary font-bold'>
                  New channel
                </span>
                <span className='size-caption text-secondary'>
                  Create a new channel
                </span>
              </div>
            </div>
            <ChevronRightIcon className='text-primary h-5 w-5' />
          </button>

          {/* Loading State */}
          {loading && (
            <div className='flex items-center justify-center px-6 py-8'>
              <span className='text-secondary size-p2'>
                Loading channels...
              </span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className='flex items-center justify-center px-6 py-8'>
              <span className='text-danger size-p2'>
                Failed to load channels. Please try again.
              </span>
            </div>
          )}

          {/* Filtered Channels */}
          {!loading &&
            !error &&
            filteredChannels.map((channel) => (
              <ChannelListItem
                key={channel.id}
                channel={channel}
                isSelected={formData.selectedChannels.includes(channel.id)}
                onToggle={() => toggleChannelSelection(channel.id)}
              />
            ))}

          {/* No Results */}
          {!loading && !error && filteredChannels.length === 0 && (
            <div className='flex items-center justify-center py-8'>
              <span className='text-secondary size-p2'>No channels found</span>
            </div>
          )}
        </div>
      </div>

      {/* Add Channel Dialog */}
      <AddChannelDialog
        open={showAddChannelDialog}
        onOpenChange={setShowAddChannelDialog}
        onSuccess={handleChannelCreated}
        isSubdialog={true}
        backButtonText='Back to channels'
      />
    </>
  );
}

interface ChannelListItemProps {
  channel: NotificationChannel;
  isSelected: boolean;
  onToggle: () => void;
}

function ChannelListItem({
  channel,
  isSelected,
  onToggle,
}: ChannelListItemProps) {
  const getChannelIcon = () => {
    switch (channel.channelType) {
      case 'Teams':
        return <TeamsIcon className='text-primary h-5 w-5' />;
      case 'Email':
        return <MailIcon className='text-primary h-5 w-5' />;
      default:
        return null;
    }
  };

  return (
    <button
      type='button'
      className='hover:bg-surface-primary-support/50 flex w-full cursor-pointer
        items-start gap-4 px-6 py-3 text-left transition-colors outline-none
        focus-visible:ring-1 focus-visible:ring-blue-500
        focus-visible:ring-inset'
      onClick={onToggle}
    >
      <Checkbox checked={isSelected} className='mt-0.5' tabIndex={-1} />
      <div className='flex flex-1 flex-col'>
        <span className='size-p2 text-primary font-bold'>{channel.name}</span>
        <span className='size-caption text-secondary'>
          {channel.channelType}
        </span>
      </div>
      {getChannelIcon()}
    </button>
  );
}
